import { Button, Checkbox, Collapse, Descriptions, Flex, message, Modal, Select, Table } from 'antd'
import { Appointment } from '../type/appointment'
import { confirm, translate } from '../../../core'
import { useMutation, useQuery } from '@apollo/client'
import {
  DeleteAppointmentMutation,
  GetAppointmentQuery,
  GetAppointmentReportQuery,
  GetNewAppointmentCountQuery,
  SetAppointmentAsSeenMutation,
  SetAppointmentServiceTypeMutation,
  ToggleReplyAppointmentMutation
} from '../graphql/appointment.graphql'
import { GetActiveServiceTypesQuery } from '../graphql/serviceType.graphql'
import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { dateDisplay, dateFormatAndTimeSlashed } from '../../../core/until'
import { InputCard } from '../../inventory/component/InputCard'
import { DeleteOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons'
import { AppointmentReport } from './AppointmentReport'

export const AppointmentList = () => {
  const [messageApi, contextHolder] = message.useMessage()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<number>(0)

  const [searchParams, setSearchParams] = useSearchParams()
  const serviceTypeFilter = useMemo(() => {
    const raw = searchParams.get('serviceTypes')
    return raw ? raw.split(',').map(Number).filter(it => !Number.isNaN(it)) : []
  }, [searchParams])

  const applyServiceTypeFilter = (ids: number[]) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (ids.length)
        next.set('serviceTypes', ids.join(','))
      else
        next.delete('serviceTypes')
      return next
    }, {replace: true})
    setCurrentPage(0)
  }

  const {data, loading} = useQuery(GetAppointmentQuery, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
        serviceTypeIds: serviceTypeFilter.length ? serviceTypeFilter : undefined,
      }
    }
  })
  const [setAsSeen] = useMutation(SetAppointmentAsSeenMutation, {
    refetchQueries: [GetNewAppointmentCountQuery],
  })

  const [toggleReply] = useMutation(ToggleReplyAppointmentMutation, {
    refetchQueries: [GetAppointmentQuery],
  })

  const [deleteRow, {loading: deleting}] = useMutation(DeleteAppointmentMutation, {
    refetchQueries: [GetAppointmentQuery, GetAppointmentReportQuery],
    onCompleted: () => messageApi.success(translate('appointment', 'remove_success_message')),
    onError: error => messageApi.error(error.message),
  })

  const {data: serviceTypesData, loading: serviceTypesLoading} = useQuery(GetActiveServiceTypesQuery)

  const [setServiceType, {loading: settingServiceType}] = useMutation(SetAppointmentServiceTypeMutation, {
    refetchQueries: [GetAppointmentQuery, GetAppointmentReportQuery],
    onCompleted: () => {
      messageApi.success(translate('service_type', 'update_success_message'))
      setEditing(null)
    },
    onError: error => messageApi.error(error.message),
  })

  const openServiceTypeEdit = (record: Appointment) => {
    setSelectedServiceTypeId(record.serviceTypeId ?? 0)
    setEditing(record)
  }

  const serviceTypeOptions = () => {
    const active = serviceTypesData?.appointmentServiceTypesActive ?? []
    const options = [
      {value: 0, label: translate('service_type_unknown')},
      ...active.map(it => ({value: it.id, label: it.label})),
    ]

    if (editing?.serviceTypeId && !active.some(it => it.id === editing.serviceTypeId))
      options.push({
        value: editing.serviceTypeId,
        label: editing.serviceType ?? String(editing.serviceTypeId),
      })

    return options
  }

  const filterOptions = [
    {value: 0, label: translate('service_type_unknown')},
    ...(serviceTypesData?.appointmentServiceTypesActive ?? []).map(it => ({value: it.id, label: it.label})),
  ]

  return (
    <InputCard contained>
      <Flex justify='end' style={{marginBottom: 16}}>
        <Button
          icon={<SettingOutlined/>}
          onClick={() => navigate('/appointments/service-types')}
        >
          {translate('service_types', 'management')}
        </Button>
      </Flex>

      <Collapse
        style={{marginBottom: 16}}
        items={[
          {
            key: 'reports',
            label: translate('appointments', 'reports'),
            children: (
              <>
                <AppointmentReport/>

                <Select
                  mode='multiple'
                  allowClear
                  style={{width: '100%'}}
                  placeholder={translate('filter_by_service_type')}
                  value={serviceTypeFilter}
                  options={filterOptions}
                  loading={serviceTypesLoading}
                  onChange={applyServiceTypeFilter}
                  maxTagCount='responsive'
                />
              </>
            ),
          },
        ]}
      />

      <Table<Appointment>
        loading={loading}
        columns={[
          {
            dataIndex: 'id',
            title: translate('#'),
            width: 50,
          },
          {
            dataIndex: 'createdAt',
            title: translate('created_at'),
            width: 200,
            render: value => dateDisplay(value, dateFormatAndTimeSlashed),
          },
          {
            dataIndex: 'firstName',
            title: translate('first_name'),
            width: 150,
          },
          {
            dataIndex: 'lastName',
            title: translate('last_name'),
            width: 150,
          },
          {
            dataIndex: 'email',
            title: translate('email'),
            width: 150,
          },
          {
            dataIndex: 'phone',
            title: translate('phone'),
            width: 150,
          },
          {
            dataIndex: 'serviceType',
            title: translate('service_type'),
            width: 200,
            render: (value: string | null, record) => (
              <Button
                type='text'
                className='p-0'
                style={{height: 'auto', whiteSpace: 'normal', textAlign: 'left'}}
                icon={<EditOutlined/>}
                onClick={() => openServiceTypeEdit(record)}
              >
                {value || translate('service_type_unknown')}
              </Button>
            ),
          },
          {
            dataIndex: 'replied',
            title: translate('replied'),
            width: 100,
            render: (value, fields) => (
              <Checkbox
                checked={value}
                onClick={() => toggleReply({
                  variables: {
                    id: fields.id,
                  },
                  notifyOnNetworkStatusChange: true,
                  onCompleted: () => messageApi.success(translate('replied', 'update_success_message')),
                })}
              />
            ),
          },
          {
            dataIndex: 'id',
            title: translate('actions'),
            width: 100,
            render: (value) => (
              <Button
                loading={deleting}
                icon={<DeleteOutlined/>}
                type='text'
                shape='circle'
                onClick={async () => {
                  const result = await confirm(
                    translate('remove_confirmation_message', 'this', 'appointment', '?')
                  )

                  if (!result)
                    return

                  await deleteRow({
                    variables: {
                      id: value,
                    },
                  })
                }}
              />
            )
          }
        ]}
        dataSource={data?.appointmentsFind.data.map(it => ({key: it.id.toString(), ...it})) ?? []}
        pagination={{
          total: data?.appointmentsFind.count,
          position: ['bottomCenter'],
          pageSize: pageSize,
          showSizeChanger: true,
        }}
        onChange={e => {
          if (e.current)
            setCurrentPage(e.current - 1)

          if (e.pageSize)
            setPageSize(e.pageSize)
        }}
        expandable={{
          onExpand: (expanded, record) => {
            if (!expanded)
              return

            if (record.seen)
              return

            return setAsSeen({
              variables: {
                id: record.id,
              }
            })

          },
          expandedRowRender: record =>
            <Descriptions
              layout='vertical'
              bordered
              items={[
                {
                  style: {width: '30%'},
                  key: '1',
                  label: translate('when'),
                  children: record.appointmentDate,
                },
                {
                  key: '2',
                  label: translate('additional_info'),
                  children: record.additionalInfo,
                },
              ]}
            />
        }}
      />

      <Modal
        open={!!editing}
        title={translate('service_type')}
        onCancel={() => setEditing(null)}
        onOk={() => editing && setServiceType({
          variables: {
            id: editing.id,
            serviceTypeId: selectedServiceTypeId ? selectedServiceTypeId : null,
          },
        })}
        confirmLoading={settingServiceType}
        destroyOnClose
      >
        <Select
          style={{width: '100%'}}
          value={selectedServiceTypeId}
          options={serviceTypeOptions()}
          loading={serviceTypesLoading}
          onChange={setSelectedServiceTypeId}
        />
      </Modal>

      {contextHolder}
    </InputCard>
  )
}